import React, { useState, createElement } from 'react';
import { X, Calendar as CalendarIcon, Clock, Users, Mail, CheckCircle2, Sparkles, User, Download, ExternalLink } from 'lucide-react';
import { TimeSlot } from '../../types/calendar';
import { getHuecosDeOro } from '../../utils/huecosDeOro';
import { useDataStore } from '../../context/DataStoreContext';
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDate?: Date;
  leadId?: string;
  leadName?: string;
  decisorEmail?: string;
}
export function BookingModal({
  isOpen,
  onClose,
  initialDate = new Date(),
  leadId = 'L-UNKNOWN',
  leadName = '',
  decisorEmail = ''
}: BookingModalProps) {
  const [step, setStep] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [duration, setDuration] = useState(30);
  const [sendFromSetter, setSendFromSetter] = useState(true);
  const [email, setEmail] = useState(decisorEmail);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const {
    createEvent
  } = useDataStore();
  const huecos = getHuecosDeOro(initialDate, duration);
  if (!isOpen) return null;
  const handleNext = () => setStep(2);
  const handleBack = () => setStep(1);
  const handleConfirm = () => {
    if (selectedSlot) {
      createEvent({
        leadId,
        leadName,
        title: subject || 'Reunión',
        type: 'Demo',
        start: selectedSlot.start,
        end: selectedSlot.end,
        attendees: [{
          email: email || 'decisor@restaurante.com',
          status: 'tentative'
        }],
        organizer: 'Ivana Jovanoska Risteska',
        status: 'confirmed',
        description: message
      });
      // Auto-open Google Calendar so the event syncs immediately to ivana@growthly-co-sys.com
      setTimeout(() => handleOpenGoogleCalendar(), 200);
    }
    setStep(3);
  };
  const formatSlot = (slot: TimeSlot) => {
    const start = new Date(slot.start);
    const end = new Date(slot.end);
    return `${start.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    })} • ${start.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })} - ${end.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };
  const generateIcsContent = () => {
    if (!selectedSlot) return '';
    const start = new Date(selectedSlot.start).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const end = new Date(selectedSlot.end).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Growthly//CRM//EN
BEGIN:VEVENT
UID:evt-${Date.now()}
DTSTART:${start}
DTEND:${end}
SUMMARY:${subject || 'Reunión'}
DESCRIPTION:${message.replace(/\n/g, '\\n')}
ORGANIZER;CN=Ivana Jovanoska Risteska:mailto:ivana@growthly-co-sys.com
ATTENDEE;RSVP=TRUE:mailto:${email}
END:VEVENT
END:VCALENDAR`;
  };
  const handleDownloadIcs = () => {
    const content = generateIcsContent();
    const blob = new Blob([content], {
      type: 'text/calendar;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'invite.ics');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleOpenGoogleCalendar = () => {
    if (!selectedSlot) return;
    const start = new Date(selectedSlot.start).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const end = new Date(selectedSlot.end).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const url = new URL('https://calendar.google.com/calendar/render');
    url.searchParams.append('action', 'TEMPLATE');
    url.searchParams.append('text', subject || 'Reunión');
    url.searchParams.append('dates', `${start}/${end}`);
    url.searchParams.append('details', message);
    url.searchParams.append('add', email);
    url.searchParams.append('add', 'ivana@growthly-co-sys.com');
    url.searchParams.append('src', 'ivana@growthly-co-sys.com');
    window.open(url.toString(), '_blank');
  };
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-62">
      <div className="bg-growthly-surface border border-white/10 rounded-xl shadow-2xl max-w-[560px] w-full overflow-hidden flex flex-col max-h-[90vh]" data-id="element-63">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-growthly-panel" data-id="element-64">
          <h2 className="text-[18px] font-semibold text-growthly-text" data-id="element-65">
            {step === 1 ? 'Agendar reunión' : step === 2 ? 'Detalles de la invitación' : '¡Reunión agendada!'}
          </h2>
          {step !== 3 && <button onClick={onClose} className="text-growthly-muted hover:text-growthly-text transition-colors" data-id="element-66">
              <X className="w-5 h-5" data-id="element-67" />
            </button>}
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar" data-id="element-68">
          {/* STEP 1: Slot Selection */}
          {step === 1 && <div className="space-y-6" data-id="element-69">
              <div data-id="element-70">
                <div className="flex items-center justify-between mb-3" data-id="element-71">
                  <label className="text-[13px] font-medium text-growthly-text flex items-center" data-id="element-72">
                    <Sparkles className="w-4 h-4 text-growthly-amber mr-2" data-id="element-73" />
                    Huecos de Oro sugeridos
                  </label>
                  <span className="text-[11px] text-growthly-muted" data-id="element-74">
                    Europe/Madrid
                  </span>
                </div>
                <div className="space-y-2" data-id="element-75">
                  {huecos.map((slot, idx) => <div key={idx} onClick={() => setSelectedSlot(slot)} className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center justify-between ${selectedSlot?.start === slot.start ? 'bg-growthly-amber/10 border-growthly-amber text-growthly-amber' : 'bg-growthly-bg border-white/5 text-growthly-text hover:border-white/20'}`} data-id="element-76">
                      <div className="flex items-center" data-id="element-77">
                        <CalendarIcon className="w-4 h-4 mr-3 opacity-70" data-id="element-78" />
                        <span className="text-[13px] font-medium" data-id="element-79">
                          {formatSlot(slot)}
                        </span>
                      </div>
                      {idx === 0 && <span className="text-[10px] bg-growthly-amber/20 text-growthly-amber px-2 py-0.5 rounded" data-id="element-80">
                          ⭐ Recomendado
                        </span>}
                    </div>)}
                </div>
              </div>

              <div data-id="element-81">
                <label className="text-[13px] font-medium text-growthly-text mb-3 block" data-id="element-82">
                  Duración
                </label>
                <div className="flex space-x-2" data-id="element-83">
                  {[15, 30, 45, 60].map(mins => <button key={mins} onClick={() => setDuration(mins)} className={`px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${duration === mins ? 'bg-white/10 text-growthly-text border border-white/20' : 'bg-growthly-bg text-growthly-muted border border-white/5 hover:bg-white/5'}`} data-id="element-84">
                      {mins} min
                    </button>)}
                </div>
              </div>
            </div>}

          {/* STEP 2: Attendees & Message */}
          {step === 2 && <div className="space-y-5" data-id="element-85">
              <div className="bg-growthly-bg border border-white/5 rounded-lg p-4 space-y-4" data-id="element-86">
                <div data-id="element-87">
                  <label className="text-[12px] font-medium text-growthly-muted mb-2 block flex items-center" data-id="element-88">
                    <Users className="w-3 h-3 mr-1.5" data-id="element-89" /> Asistentes
                  </label>
                  <div className="flex items-center space-x-2 mb-2" data-id="element-90">
                    <div className="w-6 h-6 rounded-full bg-growthly-panel flex items-center justify-center" data-id="element-91">
                      <User className="w-3 h-3 text-growthly-muted" data-id="element-92" />
                    </div>
                    <span className="text-[13px] text-growthly-text" data-id="element-93">
                      ivana@growthly-co-sys.com (Organizador)
                    </span>
                  </div>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email del decisor..." className="input-field mt-2" data-id="element-94" />
                </div>
              </div>

              <div data-id="element-95">
                <label className="text-[12px] font-medium text-growthly-muted mb-1 block" data-id="element-96">
                  Asunto
                </label>
                <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className="input-field" data-id="element-97" />
              </div>

              <div data-id="element-98">
                <label className="text-[12px] font-medium text-growthly-muted mb-1 block" data-id="element-99">
                  Mensaje (.ics description)
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} className="input-field resize-none" data-id="element-100" />
              </div>

              <div className="flex items-start bg-growthly-panel p-3 rounded-lg border border-white/5" data-id="element-101">
                <input type="checkbox" id="sendFromSetter" checked={sendFromSetter} onChange={e => setSendFromSetter(e.target.checked)} className="mt-1 rounded border-white/20 bg-growthly-bg text-growthly-amber focus:ring-growthly-amber" data-id="element-102" />
                <label htmlFor="sendFromSetter" className="ml-3 cursor-pointer" data-id="element-103">
                  <span className="block text-[13px] font-medium text-growthly-text flex items-center" data-id="element-104">
                    <Mail className="w-3 h-3 mr-1.5" data-id="element-105" />
                    Enviar desde el correo del setter
                  </span>
                  <span className="block text-[11px] text-growthly-muted mt-0.5" data-id="element-106">
                    Requiere OAuth conectado. Si se desmarca, se enviará desde
                    el servidor central.
                  </span>
                </label>
              </div>
            </div>}

          {/* STEP 3: Confirmation */}
          {step === 3 && <div className="text-center py-6" data-id="element-107">
              <div className="w-16 h-16 bg-growthly-green/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-108">
                <CheckCircle2 className="w-8 h-8 text-growthly-green" data-id="element-109" />
              </div>
              <h3 className="text-[20px] font-semibold text-growthly-text mb-2" data-id="element-110">
                Reunión agendada
              </h3>
              <p className="text-[13px] text-growthly-muted mb-6" data-id="element-111">
                El evento ha sido guardado en el CRM.
              </p>

              <div className="bg-growthly-bg border border-white/5 rounded-lg p-4 text-left space-y-3 mb-6" data-id="element-112">
                <div className="flex items-center text-[13px] text-growthly-text" data-id="element-113">
                  <CalendarIcon className="w-4 h-4 mr-3 text-growthly-muted" data-id="element-114" />
                  {selectedSlot ? formatSlot(selectedSlot) : ''}
                </div>
                <div className="flex items-center text-[13px] text-growthly-text" data-id="element-115">
                  <Users className="w-4 h-4 mr-3 text-growthly-muted" data-id="element-116" />
                  ivana@growthly-co-sys.com, {email || 'Sin email'}
                </div>
              </div>

              <div className="flex flex-col space-y-3" data-id="element-117">
                <p className="text-[11px] text-growthly-amber mb-1" data-id="element-118">
                  Asegúrate de estar conectada a Google con la cuenta
                  ivana@growthly-co-sys.com para que el evento se cree en tu
                  calendario.
                </p>
                <button onClick={handleOpenGoogleCalendar} className="w-full flex items-center justify-center px-4 py-2 bg-white text-[#0B0B0B] rounded-lg text-[13px] font-medium hover:bg-white/90 transition-colors" data-id="element-119">
                  <ExternalLink className="w-4 h-4 mr-2" data-id="element-120" />
                  Añadir a Google Calendar
                </button>
                <button onClick={handleDownloadIcs} className="w-full flex items-center justify-center px-4 py-2 border border-white/10 bg-growthly-surface text-white rounded-lg text-[13px] font-medium hover:bg-white/5 transition-colors" data-id="element-121">
                  <Download className="w-4 h-4 mr-2" data-id="element-122" />
                  Descargar archivo .ics
                </button>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/5 bg-growthly-panel flex justify-end space-x-3" data-id="element-123">
          {step === 1 && <>
              <button onClick={onClose} className="btn-secondary" data-id="element-124">
                Cancelar
              </button>
              <button onClick={handleNext} disabled={!selectedSlot} className="btn-primary" data-id="element-125">
                Siguiente →
              </button>
            </>}
          {step === 2 && <>
              <button onClick={handleBack} className="btn-secondary" data-id="element-126">
                ← Atrás
              </button>
              <button onClick={handleConfirm} className="btn-primary bg-growthly-green text-white hover:bg-growthly-green/90" data-id="element-127">
                Confirmar y agendar
              </button>
            </>}
          {step === 3 && <button onClick={onClose} className="btn-secondary" data-id="element-128">
              Cerrar
            </button>}
        </div>
      </div>
    </div>;
}